import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Campaign,
  CampaignCreatedEnvelope,
  CampaignDeletedEnvelope,
  CampaignDistributedEnvelope,
  CampaignEmailActivityUpdatedEnvelope,
  CampaignPausedEnvelope,
  CampaignPublishedEnvelope,
  CampaignRejectedEnvelope,
  CampaignScheduledEnvelope,
  CampaignTerminatedEnvelope,
  GetAudienceResponse,
  GetCampaignOptions,
  IdentifySenderAddressResponse,
  ListCampaignsOptions,
  ListCampaignsResponse,
  ListRecipientsOptions,
  ListRecipientsResponse,
  ListStatisticsResponse,
  PublishCampaignApplicationErrors,
  PublishCampaignOptions,
  PublishCampaignResponse,
  RecipientsActivityEnumWithLiterals,
  ReuseCampaignResponse,
  SendTestOptions,
  ValidateHtmlLinksResponse,
  ValidateLinkResponse,
  deleteCampaign as universalDeleteCampaign,
  getAudience as universalGetAudience,
  getCampaign as universalGetCampaign,
  identifySenderAddress as universalIdentifySenderAddress,
  listCampaigns as universalListCampaigns,
  listRecipients as universalListRecipients,
  listStatistics as universalListStatistics,
  pauseScheduling as universalPauseScheduling,
  publishCampaign as universalPublishCampaign,
  reschedule as universalReschedule,
  reuseCampaign as universalReuseCampaign,
  sendTest as universalSendTest,
  validateHtmlLinks as universalValidateHtmlLinks,
  validateLink as universalValidateLink,
} from './email-marketing-v1-campaign-campaigns.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/email-marketing' };

export function getCampaign(httpClient: HttpClient): GetCampaignSignature {
  return (campaignId: string, options?: GetCampaignOptions) =>
    universalGetCampaign(
      campaignId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCampaignSignature {
  /**
   * Retrieves information about an email campaign.
   * @param - Campaign ID.
   * @param - Options to use when getting a campaign.
   * @returns Campaign information.
   */
  (campaignId: string, options?: GetCampaignOptions): Promise<
    NonNullablePaths<
      Campaign,
      | `campaignId`
      | `editorType`
      | `status`
      | `visibilityStatus`
      | `distributionStatus`
      | `publishingData.landingPageUrl`
      | `publishingData.statistics.landingPage.opened`
      | `publishingData.statistics.landingPage.clicked`
      | `publishingData.statistics.emailCampaign.delivered`
      | `publishingData.statistics.emailCampaign.opened`
      | `publishingData.statistics.emailCampaign.clicked`
      | `publishingData.statistics.emailCampaign.bounced`
      | `publishingData.statistics.emailCampaign.complained`
      | `publishingData.statistics.emailCampaign.notSent`
      | `publishingData.statistics.total.mailsSent`
      | `publishingData.statistics.total.opened`
      | `publishingData.statistics.total.clicked`
      | `emailSubject`,
      5
    >
  >;
}

export function listCampaigns(httpClient: HttpClient): ListCampaignsSignature {
  return (options?: ListCampaignsOptions) =>
    universalListCampaigns(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCampaignsSignature {
  /**
   * Retrieves a list of email campaigns.
   *
   * Default sorts by `dateUpdated` in `DESC` order.
   * @param - Options to use when getting the list of campaigns.
   */
  (options?: ListCampaignsOptions): Promise<
    NonNullablePaths<
      ListCampaignsResponse,
      | `campaigns`
      | `campaigns.${number}.campaignId`
      | `campaigns.${number}.editorType`
      | `campaigns.${number}.status`
      | `campaigns.${number}.visibilityStatus`
      | `campaigns.${number}.distributionStatus`
      | `campaigns.${number}.publishingData.landingPageUrl`
      | `campaigns.${number}.emailSubject`,
      5
    >
  >;
}

export function publishCampaign(
  httpClient: HttpClient
): PublishCampaignSignature {
  return (campaignId: string, options?: PublishCampaignOptions) =>
    universalPublishCampaign(
      campaignId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishCampaignSignature {
  /**
   * Publishes and sends a specified campaign.
   *
   * If the `email_distribution_options` object isn't passed in the request, the campaign is only published as a landing page.
   * @param - Campaign ID
   * @param - Options to use when publishing a campaign.
   */
  (campaignId: string, options?: PublishCampaignOptions): Promise<
    NonNullablePaths<
      PublishCampaignResponse,
      | `publishingData.landingPageUrl`
      | `publishingData.statistics.landingPage.opened`
      | `publishingData.statistics.landingPage.clicked`
      | `publishingData.statistics.emailCampaign.delivered`
      | `publishingData.statistics.emailCampaign.opened`
      | `publishingData.statistics.emailCampaign.clicked`
      | `publishingData.statistics.emailCampaign.bounced`
      | `publishingData.statistics.emailCampaign.complained`
      | `publishingData.statistics.emailCampaign.notSent`
      | `publishingData.statistics.total.mailsSent`
      | `publishingData.statistics.total.opened`
      | `publishingData.statistics.total.clicked`,
      5
    > & {
      __applicationErrorsType?: PublishCampaignApplicationErrors;
    }
  >;
}

export function sendTest(httpClient: HttpClient): SendTestSignature {
  return (
    campaignId: string,
    options?: NonNullablePaths<SendTestOptions, `toEmailAddress`, 2>
  ) =>
    universalSendTest(
      campaignId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendTestSignature {
  /**
   * Sends a test email. Call this method to preview your email campaign.
   *
   * We strongly recommend calling this method only a few times in a row, as there is a rate limit to prevent abuse of the Send Test method.
   * @param - Campaign ID.
   * @param - Options for sending a test email.
   */
  (
    campaignId: string,
    options?: NonNullablePaths<SendTestOptions, `toEmailAddress`, 2>
  ): Promise<void>;
}

export function pauseScheduling(
  httpClient: HttpClient
): PauseSchedulingSignature {
  return (campaignId: string) =>
    universalPauseScheduling(
      campaignId,
      // @ts-ignore
      { httpClient }
    );
}

interface PauseSchedulingSignature {
  /**
   * Pauses a scheduled campaign.
   * @param - Campaign ID.
   */
  (campaignId: string): Promise<void>;
}

export function reschedule(httpClient: HttpClient): RescheduleSignature {
  return (campaignId: string, sendAt: Date) =>
    universalReschedule(
      campaignId,
      sendAt,
      // @ts-ignore
      { httpClient }
    );
}

interface RescheduleSignature {
  /**
   * Changes the sending time for an already scheduled campaign.
   * @param - ID of the campaign to reschedule.
   * @param - New time for sending out the campaign.
   */
  (campaignId: string, sendAt: Date): Promise<void>;
}

export function deleteCampaign(
  httpClient: HttpClient
): DeleteCampaignSignature {
  return (campaignId: string) =>
    universalDeleteCampaign(
      campaignId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCampaignSignature {
  /**
   * Permanently deletes a campaign.
   * @param - Campaign ID.
   */
  (campaignId: string): Promise<void>;
}

export function reuseCampaign(httpClient: HttpClient): ReuseCampaignSignature {
  return (campaignId: string) =>
    universalReuseCampaign(
      campaignId,
      // @ts-ignore
      { httpClient }
    );
}

interface ReuseCampaignSignature {
  /**
   * Creates a copy of an existing campaign.
   * @param - ID of the message to be duplicated.
   */
  (campaignId: string): Promise<
    NonNullablePaths<
      ReuseCampaignResponse,
      | `campaign.campaignId`
      | `campaign.editorType`
      | `campaign.status`
      | `campaign.visibilityStatus`
      | `campaign.distributionStatus`
      | `campaign.publishingData.landingPageUrl`
      | `campaign.publishingData.statistics.landingPage.opened`
      | `campaign.publishingData.statistics.landingPage.clicked`
      | `campaign.publishingData.statistics.emailCampaign.delivered`
      | `campaign.publishingData.statistics.emailCampaign.opened`
      | `campaign.publishingData.statistics.emailCampaign.clicked`
      | `campaign.publishingData.statistics.emailCampaign.bounced`
      | `campaign.publishingData.statistics.emailCampaign.complained`
      | `campaign.publishingData.statistics.emailCampaign.notSent`
      | `campaign.publishingData.statistics.total.mailsSent`
      | `campaign.publishingData.statistics.total.opened`
      | `campaign.publishingData.statistics.total.clicked`
      | `campaign.emailSubject`,
      6
    >
  >;
}

export function getAudience(httpClient: HttpClient): GetAudienceSignature {
  return (campaignId: string) =>
    universalGetAudience(
      campaignId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAudienceSignature {
  /**
   * Retrieves the audience of a paused campaign. Use this method to retrieve all selected audience of the paused campaign at once, so they can be passed in bulk when the campaign is published again after being paused.
   * @param - Campaign ID.
   */
  (campaignId: string): Promise<
    NonNullablePaths<
      GetAudienceResponse,
      | `audience.contactIds`
      | `audience.labelIds`
      | `audience.segmentIds`
      | `audience.activeContactsOnly`,
      3
    >
  >;
}

export function identifySenderAddress(
  httpClient: HttpClient
): IdentifySenderAddressSignature {
  return (emailAddress: string) =>
    universalIdentifySenderAddress(
      emailAddress,
      // @ts-ignore
      { httpClient }
    );
}

interface IdentifySenderAddressSignature {
  /**
   * Checks if the sender's email address will be used as the "from" address or will it be replaced and to what exactly.
   * @param - Email address to verify.
   */
  (emailAddress: string): Promise<
    NonNullablePaths<IdentifySenderAddressResponse, `senderAddress`, 2>
  >;
}

export function validateLink(httpClient: HttpClient): ValidateLinkSignature {
  return (url: string) =>
    universalValidateLink(
      url,
      // @ts-ignore
      { httpClient }
    );
}

interface ValidateLinkSignature {
  /**
   * Validates any provided link. Call this method when you want to check whether the link complies with the abuse rules and can be used in a campaign.
   *
   * If you provide a link as a placeholder, it's returned in a response as valid.
   * @param - URL to validate.
   */
  (url: string): Promise<NonNullablePaths<ValidateLinkResponse, `valid`, 2>>;
}

export function validateHtmlLinks(
  httpClient: HttpClient
): ValidateHtmlLinksSignature {
  return (html: string) =>
    universalValidateHtmlLinks(
      html,
      // @ts-ignore
      { httpClient }
    );
}

interface ValidateHtmlLinksSignature {
  /**
   * Validates links that are inside the provided HTML. This method automatically takes out links from the HTML block and validates them. Call this method when you want to check whether the link complies with the abuse rules and can be used in a campaign.
   * @param - HTML string with links.
   */
  (html: string): Promise<
    NonNullablePaths<ValidateHtmlLinksResponse, `blacklistedLinks`, 2>
  >;
}

export function listStatistics(
  httpClient: HttpClient
): ListStatisticsSignature {
  return (campaignIds: string[]) =>
    universalListStatistics(
      campaignIds,
      // @ts-ignore
      { httpClient }
    );
}

interface ListStatisticsSignature {
  /**
   * Retrieves a list of statistics for up to 100 selected campaigns.
   *
   * For each campaign, you receive the total activity count for the campaign's landing page and email. For example, the total
   * amount of times the landing page was opened, or the total amount of email recipients that clicked a link in an email.
   *
   * Call List Campaigns to retrieve additional information for a site's campaigns.
   * Call List Recipients to retrieve a list of recipients and their activities related to a selected campaign.
   * @param - IDs of the campaigns to retrieve (max 100 campaigns).
   */
  (campaignIds: string[]): Promise<
    NonNullablePaths<
      ListStatisticsResponse,
      | `statistics`
      | `statistics.${number}.campaignId`
      | `statistics.${number}.landingPage.opened`
      | `statistics.${number}.landingPage.clicked`
      | `statistics.${number}.email.delivered`
      | `statistics.${number}.email.opened`
      | `statistics.${number}.email.clicked`
      | `statistics.${number}.email.bounced`
      | `statistics.${number}.email.complained`
      | `statistics.${number}.email.notSent`,
      5
    >
  >;
}

export function listRecipients(
  httpClient: HttpClient
): ListRecipientsSignature {
  return (
    campaignId: string,
    activity: RecipientsActivityEnumWithLiterals,
    options?: ListRecipientsOptions
  ) =>
    universalListRecipients(
      campaignId,
      activity,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListRecipientsSignature {
  /**
   * Retrieves a list of recipients for a selected campaign based on a specific recipient activity.
   *
   * Pages are returned with a maximum of 1,000 recipients per page and defaults to 40 recipients per page.
   *
   * Call List Statistics to retrieve a list of activity for selected campaigns.
   * Call List Campaigns to retrieve additional information for your campaigns.
   *
   * If no `activity` is included, this method returns an error.
   * @param - Campaign ID.
   * @param - Email activity to filter.
   * @param - Options to use when getting the list of recipients.
   */
  (
    campaignId: string,
    activity: RecipientsActivityEnumWithLiterals,
    options?: ListRecipientsOptions
  ): Promise<
    NonNullablePaths<
      ListRecipientsResponse,
      | `recipients`
      | `recipients.${number}.contactId`
      | `recipients.${number}.emailAddress`
      | `recipients.${number}.contactDeleted`,
      4
    >
  >;
}

export const onCampaignDistributedEvent = EventDefinition(
  'wix.email_marketing.v1.campaign_campaign_distributed_event',
  true,
  (event: CampaignDistributedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignDistributedEnvelope>();
export const onCampaignPausedEvent = EventDefinition(
  'wix.email_marketing.v1.campaign_campaign_paused_event',
  true,
  (event: CampaignPausedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignPausedEnvelope>();
export const onCampaignPublishedEvent = EventDefinition(
  'wix.email_marketing.v1.campaign_campaign_published_event',
  true,
  (event: CampaignPublishedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignPublishedEnvelope>();
export const onCampaignRejectedEvent = EventDefinition(
  'wix.email_marketing.v1.campaign_campaign_rejected_event',
  true,
  (event: CampaignRejectedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignRejectedEnvelope>();
export const onCampaignScheduledEvent = EventDefinition(
  'wix.email_marketing.v1.campaign_campaign_scheduled_event',
  true,
  (event: CampaignScheduledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.sendAt' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignScheduledEnvelope>();
export const onCampaignTerminatedEvent = EventDefinition(
  'wix.email_marketing.v1.campaign_campaign_terminated_event',
  true,
  (event: CampaignTerminatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignTerminatedEnvelope>();
export const onCampaignCreated = EventDefinition(
  'wix.email_marketing.v1.campaign_created',
  true,
  (event: CampaignCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.dateCreated' },
            { path: 'entity.dateUpdated' },
            { path: 'entity.dateScheduled' },
            { path: 'entity.publishingData.datePublished' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CampaignCreatedEnvelope>();
export const onCampaignDeleted = EventDefinition(
  'wix.email_marketing.v1.campaign_deleted',
  true,
  (event: CampaignDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.dateCreated' },
            { path: 'undefined.dateUpdated' },
            { path: 'undefined.dateScheduled' },
            { path: 'undefined.publishingData.datePublished' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CampaignDeletedEnvelope>();
export const onCampaignEmailActivityUpdated = EventDefinition(
  'wix.email_marketing.v1.campaign_email_activity_updated',
  true,
  (event: CampaignEmailActivityUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<CampaignEmailActivityUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActivityType,
  ArchiveCampaignRequest,
  ArchiveCampaignResponse,
  Archived,
  Attachment,
  AutomationTranslatorContent,
  BaseEventMetadata,
  Campaign,
  CampaignAudience,
  CampaignCreatedEnvelope,
  CampaignDeletedEnvelope,
  CampaignDistributedEnvelope,
  CampaignEditorType,
  CampaignEditorTypeEnum,
  CampaignEmailActivityUpdatedEnvelope,
  CampaignPausedEnvelope,
  CampaignPublishedEnvelope,
  CampaignRecipientDetails,
  CampaignRejectedEnvelope,
  CampaignScheduledEnvelope,
  CampaignSendingStateEnum,
  CampaignStatistics,
  CampaignStatisticsContainer,
  CampaignStatusEnum,
  CampaignTerminatedEnvelope,
  CampaignTypeEnum,
  CampaignVisibilityStatusEnum,
  Click,
  Composer,
  Contact,
  CopyUserTemplateToSiteRequest,
  CopyUserTemplateToSiteResponse,
  CountCampaignsRequest,
  CountCampaignsResponse,
  CreateCampaignRequest,
  CreateCampaignResponse,
  CreateFromTemplateRequest,
  CreateFromTemplateResponse,
  CreateFromUserTemplateRequest,
  CreateFromUserTemplateResponse,
  CreateUserTemplateRequest,
  CreateUserTemplateResponse,
  CursorPaging,
  Cursors,
  DateTime,
  Decimal,
  DefaultValues,
  DeleteCampaignRequest,
  DeleteCampaignResponse,
  Distributed,
  DistributionStatistics,
  DomainEvent,
  DomainEventBodyOneOf,
  EmailActivityUpdated,
  EmailActivityUpdatedEventTypeOneOf,
  EmailDistributionOptions,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Enum,
  EstimateAudienceSizeRequest,
  EstimateAudienceSizeResponse,
  EstimateFilterSizeRequest,
  EstimateFilterSizeResponse,
  EventMetadata,
  GetAudienceRequest,
  GetAudienceResponse,
  GetAutomationTranslatorContentRequest,
  GetAutomationTranslatorContentResponse,
  GetCampaignMappingRequest,
  GetCampaignMappingResponse,
  GetCampaignOptions,
  GetCampaignRequest,
  GetCampaignResponse,
  GetComposerRequest,
  GetComposerResponse,
  GetDefaultComponentsRequest,
  GetDefaultComponentsResponse,
  GetLabelsRequest,
  GetLabelsResponse,
  GetPlaceholderKeysRequest,
  GetPlaceholderKeysResponse,
  GetUsedPlaceholderKeysRequest,
  GetUsedPlaceholderKeysResponse,
  HardBounce,
  Html,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentifySenderAddressRequest,
  IdentifySenderAddressResponse,
  Integer,
  Label,
  LandingPageStatistics,
  ListCampaignsOptions,
  ListCampaignsRequest,
  ListCampaignsResponse,
  ListRecipientsOptions,
  ListRecipientsRequest,
  ListRecipientsResponse,
  ListStatisticsRequest,
  ListStatisticsResponse,
  LocalDate,
  LookupCampaignMappingRequest,
  LookupCampaignMappingResponse,
  Map,
  MessageEnvelope,
  Money,
  Open,
  Paging,
  PagingMetadataV2,
  PauseSchedulingRequest,
  PauseSchedulingResponse,
  Paused,
  PlaceholderContent,
  PlaceholderContentEnum,
  PlaceholderContentValueOneOf,
  PlainText,
  PreviewCampaignRequest,
  PreviewCampaignResponse,
  PublishCampaignOptions,
  PublishCampaignRequest,
  PublishCampaignResponse,
  Published,
  PublishingData,
  RecipientsActivityEnum,
  ReconcileContactRequest,
  ReconcileContactResponse,
  Rejected,
  RejectionData,
  RejectionReasonEnum,
  RescheduleRequest,
  RescheduleResponse,
  ResendToNonOpenersRequest,
  ResendToNonOpenersResponse,
  RestoreInfo,
  ReuseCampaignRequest,
  ReuseCampaignResponse,
  Scheduled,
  SearchContactsRequest,
  SearchContactsResponse,
  SendTestBulkRequest,
  SendTestBulkResponse,
  SendTestOptions,
  SendTestRequest,
  SendTestResponse,
  SoftBounce,
  SubscribeFromLandingPageRequest,
  SubscribeFromLandingPageResponse,
  Terminated,
  TotalStatistics,
  UnarchiveCampaignRequest,
  UnarchiveCampaignResponse,
  Unarchived,
  UpdateComposerRequest,
  UpdateComposerResponse,
  UpdateTitleRequest,
  UpdateTitleResponse,
  UpsertAutomationTranslatorContentRequest,
  UpsertAutomationTranslatorContentResponse,
  UpsertTranslationRequest,
  UpsertTranslationResponse,
  ValidateHtmlLinksRequest,
  ValidateHtmlLinksResponse,
  ValidateLinkRequest,
  ValidateLinkResponse,
  WebhookIdentityType,
  _Array,
} from './email-marketing-v1-campaign-campaigns.universal.js';
